'use client';

import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { m } from 'framer-motion';
import { PanelLeftClose } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * MetadataSidebar Component
 *
 * A collapsible and resizable sidebar for displaying document metadata.
 * Based on the ReferenceSidebar pattern from the research page.
 *
 * Features:
 * - Collapsible: 40px collapsed width with vertical text
 * - Resizable: 280-600px width range, default 360px
 * - Hover edge: 1px visible, 3px clickable, purple highlight on hover
 * - Double-click edge to toggle collapse
 * - Drag resize with requestAnimationFrame optimization
 * - localStorage persistence for width and collapse state
 * - Framer Motion animations (0.2s duration)
 * - Mobile: Hidden below 890px breakpoint (lg)
 */

interface MetadataSidebarProps {
  title: string;
  children: React.ReactNode;
  className?: string;
}

// Resizable sidebar constants
const SIDEBAR_MIN_WIDTH = 280;
const SIDEBAR_MAX_WIDTH = 600;
const SIDEBAR_DEFAULT_WIDTH = 360;
const SIDEBAR_WIDTH_STORAGE_KEY = 'document-metadata-sidebar-width';
const SIDEBAR_COLLAPSED_STORAGE_KEY = 'document-metadata-sidebar-collapsed';

export function MetadataSidebar({
  title,
  children,
  className,
}: MetadataSidebarProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_DEFAULT_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const hasResizedRef = useRef(false);
  const resizeStartXRef = useRef(0);
  const resizeStartWidthRef = useRef(SIDEBAR_DEFAULT_WIDTH);
  const dragStartXRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);
  const t = useTranslations('Common');

  // Load saved state from localStorage on mount
  useEffect(() => {
    // Load collapsed state
    const savedCollapsed = localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY);
    if (savedCollapsed !== null) {
      setIsExpanded(savedCollapsed !== 'true');
    }

    // Load saved width
    const savedWidth = localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
    if (savedWidth) {
      const width = Number.parseInt(savedWidth, 10);
      if (width >= SIDEBAR_MIN_WIDTH && width <= SIDEBAR_MAX_WIDTH) {
        setSidebarWidth(width);
        hasResizedRef.current = true;
      }
    }
  }, []);

  // Save collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem(
      SIDEBAR_COLLAPSED_STORAGE_KEY,
      (!isExpanded).toString()
    );
  }, [isExpanded]);

  // Set CSS variable for positioning other elements if needed
  useEffect(() => {
    if (isExpanded) {
      document.documentElement.style.setProperty(
        '--document-metadata-sidebar-width',
        `${sidebarWidth}px`
      );
    } else {
      document.documentElement.style.setProperty(
        '--document-metadata-sidebar-width',
        '40px'
      );
    }

    return () => {
      document.documentElement.style.removeProperty(
        '--document-metadata-sidebar-width'
      );
    };
  }, [sidebarWidth, isExpanded]);

  // Cleanup RAF on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  // Resize handlers
  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);
      hasResizedRef.current = true;
      resizeStartXRef.current = e.clientX;
      resizeStartWidthRef.current = sidebarWidth;
    },
    [sidebarWidth]
  );

  const handleResizeMove = useCallback((e: MouseEvent) => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(() => {
      const deltaX = resizeStartXRef.current - e.clientX;
      const newWidth = resizeStartWidthRef.current + deltaX;

      // At minimum width threshold - allow collapse
      if (newWidth < SIDEBAR_MIN_WIDTH) {
        const COLLAPSE_MARGIN = 30;

        if (newWidth < SIDEBAR_MIN_WIDTH - COLLAPSE_MARGIN) {
          setIsResizing(false);
          setIsExpanded(false);
          setIsDragging(true);
          dragStartXRef.current = e.clientX;
          return;
        } else {
          const clampedWidth = SIDEBAR_MIN_WIDTH;
          if (sidebarRef.current) {
            sidebarRef.current.style.width = `${clampedWidth}px`;
          }
          document.documentElement.style.setProperty(
            '--document-metadata-sidebar-width',
            `${clampedWidth}px`
          );
          return;
        }
      }

      const clampedWidth = Math.max(
        SIDEBAR_MIN_WIDTH,
        Math.min(SIDEBAR_MAX_WIDTH, newWidth)
      );

      if (sidebarRef.current) {
        sidebarRef.current.style.width = `${clampedWidth}px`;
      }

      document.documentElement.style.setProperty(
        '--document-metadata-sidebar-width',
        `${clampedWidth}px`
      );

      resizeStartWidthRef.current = clampedWidth;
      resizeStartXRef.current = e.clientX;
    });
  }, []);

  const handleResizeEnd = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    const finalWidth = sidebarRef.current
      ? Number.parseInt(sidebarRef.current.style.width, 10)
      : resizeStartWidthRef.current;

    if (isExpanded && !Number.isNaN(finalWidth)) {
      setSidebarWidth(finalWidth);
      localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, finalWidth.toString());

      if (sidebarRef.current) {
        sidebarRef.current.style.width = '';
      }
    }

    setIsResizing(false);
    setIsDragging(false);

    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, [isExpanded]);

  // Attach/detach mouse event listeners for resizing
  useEffect(() => {
    if (!isResizing) {
      return;
    }
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleResizeMove, handleResizeEnd]);

  // Drag handlers for expand (when collapsed)
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartXRef.current = e.clientX;
  }, []);

  const handleDragMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const deltaX = dragStartXRef.current - e.clientX;
      const EXPAND_THRESHOLD = 50;

      if (deltaX > EXPAND_THRESHOLD && !isExpanded) {
        setIsDragging(false);
        setIsExpanded(true);
        setSidebarWidth(SIDEBAR_MIN_WIDTH);

        setIsResizing(true);
        resizeStartXRef.current = e.clientX;
        resizeStartWidthRef.current = SIDEBAR_MIN_WIDTH;
      }
    },
    [isDragging, isExpanded]
  );

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  // Attach/detach mouse event listeners for dragging
  useEffect(() => {
    if (!isDragging) {
      return;
    }
    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  const toggleSidebar = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  // Collapsed state - vertical text
  if (!isExpanded) {
    return (
      <TooltipProvider>
        <aside
          className={cn(
            'hidden lg:flex border-l border-line-default bg-surface-sidebar relative',
            className
          )}
          aria-label={t('expandSidebar')}
          style={{ width: '40px', gridColumn: '2', height: '100%' }}
        >
          {/* Hover edge */}
          <div
            className='absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize hover:bg-brand-tertiary transition-colors z-20'
            aria-hidden='true'
            onMouseDown={handleDragStart}
            onDoubleClick={toggleSidebar}
          >
            <div className='absolute left-0 top-0 bottom-0 w-2' />
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={toggleSidebar}
                className='w-full h-full flex items-center justify-center hover:bg-surface-subtle transition-colors duration-200 cursor-pointer group'
                aria-label={t('expandSidebar')}
                aria-expanded={false}
                style={{ minHeight: '44px' }}
              >
                <div
                  className='flex items-center gap-5 text-sm font-semibold text-content-secondary group-hover:text-brand-tertiary transition-colors select-none'
                  style={{
                    writingMode: 'vertical-rl',
                    transform: 'rotate(180deg)',
                    letterSpacing: '0.05em',
                  }}
                >
                  <span>{title}</span>
                </div>
              </button>
            </TooltipTrigger>
            <TooltipContent side='left'>
              <p>{t('expandSidebar')}</p>
            </TooltipContent>
          </Tooltip>
        </aside>
      </TooltipProvider>
    );
  }

  // Expanded state - full sidebar with content
  return (
    <TooltipProvider>
      <m.aside
        ref={sidebarRef}
        initial={false}
        animate={isResizing ? false : { width: sidebarWidth }}
        transition={hasResizedRef.current ? { duration: 0 } : { duration: 0.2 }}
        style={{ gridColumn: '2' }}
        className={cn(
          'hidden lg:block border-l border-line-default bg-surface-sidebar relative overflow-hidden',
          className
        )}
        aria-label={t('collapseSidebar')}
        aria-expanded={true}
      >
        {/* Resize handle */}
        <div
          onMouseDown={handleResizeStart}
          onDoubleClick={toggleSidebar}
          className={cn(
            'absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize hover:bg-brand-tertiary transition-colors z-20 group',
            isResizing && 'bg-brand-tertiary'
          )}
          aria-label={t('resizeSidebar')}
        >
          <div className='absolute left-0 top-0 bottom-0 w-2' />
        </div>

        {/* Header */}
        <div className='sticky top-0 border-b border-line-default bg-surface-sidebar/80 backdrop-blur-sm z-10'>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                onClick={toggleSidebar}
                className='p-4 pb-2 cursor-pointer hover:bg-surface-subtle transition-colors duration-200'
                role='button'
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleSidebar();
                  }
                }}
                aria-label={t('collapseSidebar')}
              >
                <div className='flex items-center justify-between'>
                  <div className='flex items-center justify-between gap-2 flex-1 min-w-0'>
                    <div className='flex-shrink-0 p-1'>
                      <PanelLeftClose
                        className='h-5 w-5 text-content-secondary'
                        aria-hidden='true'
                      />
                    </div>
                    <h2 className='text-lg text-content-primary truncate'>
                      {title}
                    </h2>
                    <div />
                  </div>
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>{t('collapseSidebar')}</p>
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Content - subtract header height (~52px) and navbar (64px) */}
        <ScrollArea className='h-[calc(100vh-52px)] w-full'>
          <div className='px-3 py-2 w-full max-w-full overflow-hidden'>
            {children}
          </div>
        </ScrollArea>
      </m.aside>
    </TooltipProvider>
  );
}
